import type {
  ColumnAssignment,
  ColumnRole,
  DiscoveredColumn,
  DiscoveredTable,
  MappingAggregation,
} from '../services/admin-api';

/** Uso elegido para una columna; `aggregation` solo aplica a medidas. */
export interface Assignment {
  readonly role: ColumnRole;
  readonly aggregation: MappingAggregation | null;
}
export type Assignments = ReadonlyMap<string, Assignment>;

export const ROLE_LABELS: Readonly<Record<ColumnRole, string>> = {
  dimension: 'Dimension',
  measure: 'Medida',
  both: 'Ambas',
  none: 'No usar',
};

export const AGGREGATION_LABELS: Readonly<Record<MappingAggregation, string>> = {
  sum: 'Suma',
  avg: 'Promedio',
  min: 'Minimo',
  max: 'Maximo',
  count: 'Conteo',
  'count-distinct': 'Conteo distinto',
};

export const assignmentKey = (tableId: string, column: string): string =>
  `${tableId}\u0000${column}`;

/** Roles que admite una columna: sin agregaciones validas no puede ser medida. */
export const rolesFor = (column: DiscoveredColumn): readonly ColumnRole[] =>
  column.aggregations.length > 0 ? ['dimension', 'measure', 'both', 'none'] : ['dimension', 'none'];

const isMeasure = (role: ColumnRole): boolean => role === 'measure' || role === 'both';

/** Agregacion por defecto al convertir una columna en medida. */
function defaultAggregation(column: DiscoveredColumn): MappingAggregation | null {
  if (column.suggestedAggregation !== null) return column.suggestedAggregation;
  if (column.aggregations.includes('sum')) return 'sum';
  return column.aggregations[0] ?? null;
}

/** Valor inicial de cada columna a partir de la sugerencia del servidor. */
export function initialAssignments(tables: readonly DiscoveredTable[]): Assignments {
  const entries = tables.flatMap((table) =>
    table.columns.map((column): [string, Assignment] => {
      const role = rolesFor(column).includes(column.suggestedRole)
        ? column.suggestedRole
        : 'dimension';
      return [
        assignmentKey(table.id, column.name),
        { role, aggregation: isMeasure(role) ? defaultAggregation(column) : null },
      ];
    }),
  );
  return new Map(entries);
}

export function withRole(
  current: Assignment | undefined,
  role: ColumnRole,
  column: DiscoveredColumn,
): Assignment {
  if (!isMeasure(role)) return { role, aggregation: null };
  return { role, aggregation: current?.aggregation ?? defaultAggregation(column) };
}

export const assignmentOf = (
  assignments: Assignments,
  tableId: string,
  column: string,
): Assignment =>
  assignments.get(assignmentKey(tableId, column)) ?? { role: 'none', aggregation: null };

/** Cuerpo `columns` del borrador para las tablas seleccionadas. */
export function toColumnAssignments(
  tables: readonly DiscoveredTable[],
  assignments: Assignments,
): ColumnAssignment[] {
  return tables.flatMap((table) =>
    table.columns.map((column) => {
      const { role, aggregation } = assignmentOf(assignments, table.id, column.name);
      return {
        objectId: table.id,
        column: column.name,
        role,
        ...(isMeasure(role) && aggregation !== null ? { aggregation } : {}),
      };
    }),
  );
}

export interface MappingSummary {
  readonly dimensions: readonly string[];
  readonly measures: readonly string[];
  readonly unused: number;
}

/** Resumen visible: a donde va cada columna. */
export function summarize(
  tables: readonly DiscoveredTable[],
  assignments: Assignments,
): MappingSummary {
  const dimensions: string[] = [];
  const measures: string[] = [];
  let unused = 0;
  for (const table of tables) {
    for (const column of table.columns) {
      const { role, aggregation } = assignmentOf(assignments, table.id, column.name);
      if (role === 'none') unused += 1;
      if (role === 'dimension' || role === 'both') dimensions.push(column.name);
      if (isMeasure(role)) {
        const how = aggregation === null ? '' : ` (${AGGREGATION_LABELS[aggregation]})`;
        measures.push(`${column.name}${how}`);
      }
    }
  }
  return { dimensions, measures, unused };
}
